<script lang="ts" setup> 
  import { isBoolean, isString } from 'lodash';
  import { onMounted,  ref, watch, computed,nextTick } from 'vue';
  import { DefaultActionButton,IActionButton,IActionButtonIdentifier,ActionButtonStyle } from '../lib/Types/Types';

  const props = withDefaults(defineProps<{
    executing: boolean,
    id?: string;    
    baseText?: string,
    baseClass?: string,
    disabled: boolean,
    clickEvent: (input : IActionButtonIdentifier | undefined) => void,
    width?: number,
    toolTip?: string,
    iconClass?: string,
    spinText?: string, 
    spinClass?: string,
    overRideStyle?: ActionButtonStyle,
    executeInit? : boolean,
    endingPadding?: number
  }>(), {
    executing: false,
    overRideStyle: ActionButtonStyle.Base,
    executeInit: true
  });
  
  const isExecuting= ref<boolean>(false); 
  const buttonProps = ref<IActionButton>();  
  const buttonInput = ref<IActionButtonIdentifier>();   
  const buttonStyleWidth = ref<string>();
  const baseClass = ref<string>();      
  const iconClass = ref<string>();      
  const spinClass = ref<string>();      
  const buttonToolTip = ref<string>();     

  onMounted(() => {
    isExecuting.value = props.executing;
    buttonProps.value = 
      DefaultActionButton(
        {
          id: props.id, baseText: props.baseText, baseClass: props.baseClass + ((props.endingPadding ?? 0) > 0 ? ` me-${props.endingPadding}` : ""),
          disabled: props.disabled,
          clickEvent: props.clickEvent, width: props.width, toolTip: props.toolTip, 
          iconClass: props.iconClass, spinText: props.spinText, spinClass: props.spinClass,
          overRideStyle: props.overRideStyle, executeInit: props.executeInit
        } as IActionButton); 
    buttonInput.value = {id: props.id, baseText: props.baseText};
    buttonStyleWidth.value = "width: " + (buttonProps.value.width  ? buttonProps.value.width + "px;" : "100%"); 
    iconClass.value = (buttonProps.value  &&  buttonProps.value.iconClass ? buttonProps.value.iconClass : "");
    spinClass.value = (buttonProps.value  &&  buttonProps.value.spinClass ? buttonProps.value.spinClass : "");
    baseClass.value = (buttonProps.value  &&  buttonProps.value.baseClass ? buttonProps.value.baseClass : "");
    buttonToolTip.value = (buttonProps.value  &&  buttonProps.value.toolTip ? buttonProps.value.toolTip : "");
  });

  watch(() => ([props.executing, props.baseText, props.toolTip, props.disabled, props.endingPadding]) ,
                ([_new_executing, _new_text, _new_tooltip, _new_disabled, _new_padding],
                 [_old_executing, _old_text, _old_tooltip, _old_disabled, _old_padding]) => { 
    if (_new_executing !== _old_executing) {
      isExecuting.value = (isBoolean(_new_executing) ? _new_executing : isExecuting.value);
    }
    if ((_new_text !== _old_text) && isString(_new_text)) {
      if (buttonProps.value) {
        buttonProps.value.baseText = _new_text;
      }
    } 
    if ((_new_tooltip !== _old_tooltip) && isString(_new_tooltip) && (buttonProps.value)) {
      buttonProps.value.toolTip = _new_tooltip; 
      buttonToolTip.value = _new_tooltip;
    }
    if ((_new_disabled != _old_disabled) && isBoolean(_new_disabled) && (buttonProps.value)) {
      buttonProps.value.disabled = _new_disabled;
    }
    if (_new_padding != _old_padding) {
      baseClass.value = props.baseClass + (_new_padding ?? 0 > 0 ? ` me-${_new_padding}` : "");
    }
  });

  // Compute 
  const displayFaIcon = computed<boolean>(() => buttonProps.value && buttonProps.value.iconClass && !isExecuting.value  ? true : false); 
  const displaySpin = computed<boolean>(() => buttonProps.value && buttonProps.value.spinClass && isExecuting.value ? true : false);
  const buttonDisabled = computed<boolean>(() => buttonProps.value && buttonProps.value.disabled ? true : isExecuting.value );   
  
  const buttonDisplayedText = computed<string>(() => {
    if (props.overRideStyle === ActionButtonStyle.Blank) {
      return "";
    }
    let spinning_text = (buttonProps.value && buttonProps.value.spinText ? buttonProps.value.spinText : "Button");
    let displayed_text = (buttonProps.value && buttonProps.value.baseText ? buttonProps.value.baseText : "Button");
    return buttonProps.value  &&  isExecuting.value ? spinning_text : displayed_text
  }); 

  function actionButtonClick() { 
    if (buttonProps.value?.executeInit)
      isExecuting.value = true; 
    nextTick(() => {props.clickEvent(buttonInput.value)}); 
  } 
</script>
<style>
  .textual_button {
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    color: #0d6efd; 
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent; 
    transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;
    text-align: left;
    padding: 0.25rem 0.5rem;
    font-size: .875rem;
    border-radius: 0.2rem;
  }
  .textual_button:hover, .textual_button:visited {
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    color: green; 
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent; 
    transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;
    text-align: left;
    padding: 0.25rem 0.5rem;
    font-size: .875rem;
    border-radius: 0.2rem;
  }
</style>

<template>
   <button :class="baseClass" :disabled="buttonDisabled" @click="actionButtonClick" :style="buttonStyleWidth" :title="buttonToolTip">
      <em v-show="displayFaIcon" :class="iconClass"></em>
      <em v-show="displaySpin" :class="spinClass" role="status" aria-hidden="true"></em>
      {{buttonDisplayedText}}
    </button> 
</template>

    
